// ============================================================
// PEBBLE — Cookie consent: region-aware silence + the pebble
// ============================================================
// US-style opt-out regions see nothing unprompted; their legal surface
// is the top-left menu's "Cookie settings" link (which opens this same
// card, including its own "Do Not Sell or Share" button) and the
// Global Privacy Control signal, which is honored automatically.
// Opt-in regions (EEA + UK) get the pebble chip, and nothing
// non-essential runs until a choice is made. Region comes from
// window.pebbleRegion.country (theme.liquid renders it from Shopify's
// own IP-based localization.country — the same signal that drives the
// footer's country selector, so it's known-good) checked against a
// hardcoded list, rather than Shopify's customerPrivacy
// shouldShowGDPRBanner()/shouldShowBanner(), which depends on the
// merchant having configured Settings > Customer Privacy in admin and
// silently defaults to "show to everyone" when that's untouched.
// Every decision is still stored locally AND pushed into Shopify's
// customerPrivacy API so checkout, pixels, and apps respect it; a
// `pebble:consentUpdated` event fires for any future script that
// needs to gate itself.

(function () {
  var STORAGE_KEY = 'pebble_cookie_consent';

  // EU 27 + EEA (Iceland, Liechtenstein, Norway) + UK — the territories
  // where GDPR/UK GDPR/ePrivacy require consent before non-essential
  // cookies run
  var CONSENT_REQUIRED_COUNTRIES = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
    'HU', 'IE', 'IS', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL',
    'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB',
  ];

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    var root = document.getElementById('pebble-cookie');
    if (!root) return;

    var chip = root.querySelector('[data-cookie-chip]');
    var card = root.querySelector('.pebble-cookie__card');
    var toggles = root.querySelectorAll('[data-cookie-toggle]');
    var reopenLinks = document.querySelectorAll('[data-cookie-reopen]');
    var gpcActive = navigator.globalPrivacyControl === true;

    function readConsent() {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    }

    function writeConsent(consent) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
      } catch (e) {
        // Storage unavailable (private mode) — the choice still applies
        // for this page load via applyConsent, it just won't stick
      }
    }

    // Shopify ships the customerPrivacy API as a lazy feature; load it
    // before asking region questions or recording consent
    function loadConsentApi(cb) {
      if (window.Shopify && window.Shopify.customerPrivacy) {
        cb(window.Shopify.customerPrivacy);
        return;
      }
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (error) {
            cb(!error && window.Shopify.customerPrivacy ? window.Shopify.customerPrivacy : null);
          }
        );
        return;
      }
      cb(null);
    }

    // Does this visitor's law require asking before tracking? Unknown
    // (country not resolved) errs toward asking — the chip is quiet
    // enough that the safe default costs nothing.
    function regionNeedsConsent() {
      var country = window.pebbleRegion && window.pebbleRegion.country;
      if (!country) return true;
      return CONSENT_REQUIRED_COUNTRIES.indexOf(country.toUpperCase()) !== -1;
    }

    function applyConsent(consent) {
      loadConsentApi(function (api) {
        if (api && typeof api.setTrackingConsent === 'function') {
          api.setTrackingConsent(
            {
              marketing: consent.marketing,
              analytics: consent.analytics,
              preferences: consent.analytics,
              sale_of_data: consent.marketing && !gpcActive,
            },
            function () {}
          );
        }
        document.dispatchEvent(new CustomEvent('pebble:consentUpdated', { detail: consent }));
      });
    }

    function showChip() {
      root.hidden = false;
      requestAnimationFrame(function () {
        root.classList.add('is-visible');
      });
    }

    var lastOpener = null;

    // After the card hides, keyboard focus must land somewhere real —
    // left alone it falls to <body> (or worse, stays on a control
    // inside the now-hidden card). Prefer the control that opened the
    // card; the "Cookie settings" opener lives inside the utility
    // panel, which closed behind us, so fall back to that panel's
    // toggle, then to main content.
    function restoreFocus() {
      if (!root.contains(document.activeElement)) return;
      var target = null;
      if (lastOpener && lastOpener.offsetParent !== null) {
        target = lastOpener;
      } else if (lastOpener && lastOpener.closest) {
        var menuRoot = lastOpener.closest('.pebble-utility-menu');
        var toggle = menuRoot && menuRoot.querySelector('[data-utility-toggle]');
        if (toggle && toggle.offsetParent !== null) target = toggle;
      }
      if (!target) target = document.getElementById('MainContent');
      if (target) target.focus();
    }

    function hideChip() {
      restoreFocus();
      root.classList.remove('is-visible', 'is-open');
      chip.setAttribute('aria-expanded', 'false');
      window.setTimeout(function () {
        root.hidden = true;
      }, 400);
    }

    function setCardOpen(open, opener) {
      root.classList.toggle('is-open', open);
      chip.setAttribute('aria-expanded', String(open));
      if (open) {
        lastOpener = opener || chip;
        // Move focus into the dialog (card carries tabindex="-1");
        // without this, keyboard/screen-reader users are stranded on
        // the trigger while the card opens elsewhere on the page
        window.requestAnimationFrame(function () {
          card.focus();
        });
      }
    }

    function syncToggles(consent) {
      var current = consent || readConsent() || { analytics: true, marketing: true };
      toggles.forEach(function (toggle) {
        toggle.checked = !!current[toggle.getAttribute('data-cookie-toggle')];
      });
    }

    var saveBtn = root.querySelector('[data-cookie-save]');

    // Restart the confirm-flash animation on demand (removing then
    // re-adding the class), so quick repeated choices each get their
    // own flash instead of the animation silently no-opping
    function flashSave() {
      if (!saveBtn) return;
      saveBtn.classList.remove('is-confirming');
      void saveBtn.offsetWidth; // force reflow to restart the animation
      saveBtn.classList.add('is-confirming');
    }

    if (saveBtn) {
      saveBtn.addEventListener('animationend', function () {
        saveBtn.classList.remove('is-confirming');
      });
    }

    // Three-beat sequence: toggles flip, then (once that's mostly
    // settled) the Save button flashes to confirm, then the card
    // closes — each stage waited out rather than overlapping. Only
    // needed when decide() is setting the toggles itself (Allow all /
    // Essentials only / Do Not Sell) — when the visitor already set
    // them by hand and hit Save, there's nothing to wait on, so that
    // path skips straight to a quick flash-and-close.
    var TOGGLE_DELAY_MS = 100;
    var FLASH_DELAY_MS = 150; // after the toggles start, not after click
    var DECISION_PAUSE_MS = TOGGLE_DELAY_MS + FLASH_DELAY_MS + 200;
    var SAVE_PAUSE_MS = 220;

    function decide(consent, options) {
      var skipToggleSync = options && options.skipToggleSync;
      consent.necessary = true;
      consent.timestamp = Date.now();
      writeConsent(consent);
      applyConsent(consent);

      if (skipToggleSync) {
        flashSave();
        window.setTimeout(hideChip, SAVE_PAUSE_MS);
        return;
      }

      window.setTimeout(function () {
        syncToggles(consent);
      }, TOGGLE_DELAY_MS);
      window.setTimeout(flashSave, TOGGLE_DELAY_MS + FLASH_DELAY_MS);
      window.setTimeout(hideChip, DECISION_PAUSE_MS);
    }

    chip.addEventListener('click', function () {
      setCardOpen(!root.classList.contains('is-open'));
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.classList.contains('is-open')) {
        setCardOpen(false);
        chip.focus();
      }
    });

    root.querySelector('[data-cookie-accept]').addEventListener('click', function () {
      decide({ analytics: true, marketing: true });
    });

    root.querySelector('[data-cookie-reject]').addEventListener('click', function () {
      decide({ analytics: false, marketing: false });
    });

    // CCPA-required opt-out, kept distinct from "Essentials only" above
    // even though it does the same thing — the exact wording matters
    // here, since California visitors specifically look for this phrase
    root.querySelector('[data-cookie-do-not-sell]').addEventListener('click', function () {
      decide({ analytics: false, marketing: false });
    });

    root.querySelector('[data-cookie-save]').addEventListener('click', function () {
      var consent = {};
      toggles.forEach(function (toggle) {
        consent[toggle.getAttribute('data-cookie-toggle')] = toggle.checked;
      });
      decide(consent, { skipToggleSync: true });
    });

    // Top-left menu's "Cookie settings" link — the only consent surface
    // US visitors ever encounter unprompted, and the way anyone changes
    // their mind later
    reopenLinks.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        syncToggles();
        showChip();
        setCardOpen(true, link);
      });
    });

    var existingConsent = readConsent();
    if (existingConsent) {
      applyConsent(existingConsent);
      return;
    }

    if (regionNeedsConsent()) {
      // Opt-in region: show the pebble; nothing non-essential runs
      // until the visitor decides
      showChip();
    } else if (gpcActive) {
      // Opt-out region with a Global Privacy Control signal: that IS
      // the visitor's choice — record it, still no UI
      decide({ analytics: true, marketing: false });
    }
    // Opt-out region, no GPC: silence — tracking is permitted by
    // default and the footer links carry the opt-out
  }
})();
